import type { UseSpeakers } from './use-speakers';

import { Accordion, Paragraph, PlusIcon, Tooltip } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import { SpeakerProfileCard } from './speaker-profile-card';
import { MAX_SPEAKERS } from './use-speakers';

import styles from './event-speakers.module.css';

type Props = {
  speakers: UseSpeakers;
  disabled?: boolean;
  deleteDisabled?: boolean;
};

export function EventSpeakers({ speakers, disabled, deleteDisabled }: Props) {
  const { t } = useTranslation();

  const {
    cards,
    errors,
    dirtyByKey,
    uploadingByKey,
    canAddSpeaker,
    addSpeaker,
    selectImage,
    removeImage,
    changeName,
    blurName,
    changePosition,
    blurPosition,
    deleteCard,
  } = speakers;

  const atMaxSpeakers = cards.length >= MAX_SPEAKERS;
  const addDisabled = !canAddSpeaker;

  return (
    <section className={styles.wrapper} data-testid="event-speakers">
      <Paragraph>{t('manager.webcastSetup.speakers.heading')}</Paragraph>

      {cards.map((card, index) => (
        <SpeakerProfileCard
          key={card.cardKey}
          index={index}
          card={card}
          errors={errors[card.cardKey]}
          unsavedChanges={dirtyByKey[card.cardKey]}
          uploading={uploadingByKey[card.cardKey]}
          disabled={disabled}
          deleteDisabled={deleteDisabled}
          onImageSelected={selectImage}
          onImageRemoved={removeImage}
          onNameChange={changeName}
          onNameBlur={blurName}
          onPositionChange={changePosition}
          onPositionBlur={blurPosition}
          onDelete={deleteCard}
        />
      ))}

      <Tooltip label={t('manager.webcastSetup.speakers.maxReached')} hidden={!atMaxSpeakers}>
        {/* The accordion sets `pointer-events: none` on its disabled button, so the cursor and the
            tooltip hover only work via this wrapper (it catches the events the button passes through). */}
        <div className={addDisabled ? styles.addButtonDisabled : undefined}>
          <Accordion
            buttonClassName={styles.addButtonTab}
            buttonId="add-speaker-button"
            buttonLabel={t('manager.webcastSetup.speakers.addSpeaker')}
            compact
            isOpen={false}
            disabled={addDisabled}
            buttonContent={
              <>
                <PlusIcon />
                {t('manager.webcastSetup.speakers.addSpeaker')}
              </>
            }
            onToggle={() => addSpeaker()}
          />
        </div>
      </Tooltip>
    </section>
  );
}
